import { FunctionComponent, useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";

import AdminContainer from "@voxmedia/duet-framework/admin/AdminContainer";
import ContentAdsContainer from "@voxmedia/duet-framework/ads/components/ContentAdsContainer";
import ContentResourceAnalyticsContainer from "@voxmedia/duet-framework/analytics/components/ContentResourceAnalyticsContainer";
import ParselyTagsContainer from "@voxmedia/duet-framework/analytics/components/ParselyTagsContainer";
import SearchTagsContainer from "@voxmedia/duet-framework/analytics/components/SearchTagsContainer";
import ArticleSEO from "@voxmedia/duet-framework/seo/components/ArticleSEOContainer";
import SEOSchemasContainer from "@voxmedia/duet-framework/seo/components/SEOSchemasContainer";
import { MetadataContainer_feedContentResource$key } from "src/__generated__/relay/MetadataContainer_feedContentResource.graphql";
import {
  useCskLiveResourceMetadata,
  useCskLiveResourceMetadataRefresh,
} from "src/context/CskLiveResourceMetadataContext";
import CskCoralDrawer from "src/coral/CskCoralDrawer";
import { useCommentsPreviewMode } from "src/hooks/use-comments-preview-mode";
import { useCskCoralSurfaceClassName } from "src/hooks/use-csk-coral-surface-class-name";

import ZephrDataContainer from "./ZephrDataContainer";

export interface Props {
  feedContentResource: MetadataContainer_feedContentResource$key;
  /**
   * Fetches publish-time CMS fields client-side and updates the Relay store so
   * lede timestamps, comments, SEO, analytics, and Zephr use post-publish data.
   */
  refreshLiveMetadata?: boolean;
  /** @deprecated Use `refreshLiveMetadata` */
  refreshZephr?: boolean;
}

const MetadataContainer: FunctionComponent<Props> = ({
  feedContentResource,
  refreshLiveMetadata = false,
  refreshZephr = false,
}) => {
  const resource = useFragment(
    graphql`
      fragment MetadataContainer_feedContentResource on FeedContentResourceInterface {
        id
        comments {
          enabled
          mode
        }
        # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
        # of the resource cache key, so including the key is not needed
        categories @excludeCacheKey {
          title
          path
          slug
        }
        # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
        # of the resource cache key, so including the key is not needed
        internalCategories @excludeCacheKey {
          slug
        }
        ...AdminContainer_resolvedResource
        ...ArticleSEOContainer_feedContentResource
        ...ContentResourceAnalyticsContainer_contentResource
        ...ParselyTagsContainer_feedContentResource
        ...SearchTagsContainer_feedContentResource
        ...SEOSchemasContainer_feedContentResource
        ...ContentAdsContainer_contentResource
        ...CskCoralDrawer_contentResource
      }
    `,
    feedContentResource,
  );

  const { id, comments, internalCategories = [], categories = [] } = resource;
  const liveContext = useCskLiveResourceMetadata();
  const usesShellRefresh = liveContext.enabled && refreshLiveMetadata;
  const localRefreshReady = useCskLiveResourceMetadataRefresh(
    id,
    refreshLiveMetadata && !liveContext.enabled,
  );
  const liveMetadataReady = usesShellRefresh
    ? liveContext.isReady
    : localRefreshReady;
  const previewMode = useCommentsPreviewMode();
  const coralDrawerClassName = useCskCoralSurfaceClassName();
  /**
   * In dev, defer mounting Coral until after hydration so `?comments=test` can
   * apply the staging override before the embed initializes (SSR cannot read
   * the query string on static CSK pages).
   */
  const [clientReadyForCoral, setClientReadyForCoral] = useState(
    process.env.NODE_ENV !== "development",
  );
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      setClientReadyForCoral(true);
    }
  }, []);

  const metadataSettled = !refreshLiveMetadata || liveMetadataReady;
  const showCommentsDrawer =
    clientReadyForCoral &&
    metadataSettled &&
    (previewMode || (comments.enabled && comments.mode !== "qa"));

  return (
    <>
      {/*
        Mount Zephr once publish-time categories have settled. Framework Zephr
        calls zephrBrowser.run again whenever those arrays change, and a second
        run injects another cliff without removing the first. The hidden copy
        then takes the slide-up class, so the page locks with no module.
      */}
      {metadataSettled ? (
        <ZephrDataContainer
          refresh={refreshZephr && !refreshLiveMetadata}
          articleId={id}
          internalCategories={internalCategories}
          categories={categories}
        />
      ) : null}
      <AdminContainer resolvedResource={resource} />
      <ArticleSEO feedContentResource={resource} />
      <ParselyTagsContainer feedContentResource={resource} />
      <SearchTagsContainer feedContentResource={resource} />
      <ContentResourceAnalyticsContainer
        contentResource={resource}
        shouldEmitEvent={metadataSettled}
      />
      <SEOSchemasContainer feedContentResource={resource} />
      <ContentAdsContainer contentResource={resource} />
      {showCommentsDrawer && (
        <CskCoralDrawer
          contentResource={resource}
          drawerClassName={coralDrawerClassName}
        />
      )}
    </>
  );
};

export default MetadataContainer;
